import {useEffect, useState} from 'react';

import {useTheme, DialogActions, Typography} from '@mui/material';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Lottie from 'lottie-react';
import {bool, func} from 'prop-types';
import {Link, useLocation} from 'react-router-dom';

import {APP_ROUTES} from '../../../const';
import useAuth from '../../../hooks/providers/useAuth';
import usePayment from '../../../hooks/providers/usePayment';
import useProfile from '../../../hooks/providers/useProfile';
import useProjects from '../../../hooks/providers/useProjects';
import deleteAnimation from '../../../lotties/deleteAnimation.json';
import Button from '../../form/buttons/Button/Button';
import BaseModal from '../../modal/BaseModal';

const DeleteProfileModal = ({deleteProfile, onClose, isOpen = false}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleted, setIsDeleted] = useState(false);
  const auth = useAuth();
  const parentTheme = useTheme();
  const {isGuest} = usePayment();
  const {projects, fetchProjects} = useProjects();
  const {deleteError} = useProfile();
  const location = useLocation();

  const resetState = () => {
    setIsLoading(false);
    setIsDeleted(false);
    onClose();
  };

  const onConfirm = async () => {
    setIsLoading(true);
    try {
      await deleteProfile();
      setIsDeleted(true);
    } catch (e) {
      // eslint-disable-next-line no-console
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    // We should only fetch projects when user tries to delete account from profile page.
    // He can also cancel his account creation on /subscribe, but at this point, he does not own any projects
    const shouldFetchProjects = location.pathname !== APP_ROUTES.subscribe;
    if (isOpen && shouldFetchProjects) {
      (async () => {
        try {
          setIsLoading(true);
          await fetchProjects();
        } catch (e) {
          // eslint-disable-next-line no-console
          console.error(e);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [isOpen]);

  const userCanDeleteAccount = isGuest || (projects || []).length < 1;

  const title = isDeleted ? 'Compte supprimé' : 'Supprimer votre compte';
  return (
    <BaseModal
      canCloseOnBackdropClick={false}
      title={title}
      maxWidth="sm"
      onClose={resetState}
      open={isOpen}
      sx={{
        '& .MuiDialogTitle-root': {
          color: parentTheme.palette.error.main
        }
      }}
    >
      {isDeleted && !deleteError ? (
        <Grid flexDirection="column" container alignItems="center" p={3}>
          <Lottie style={{height: '160px', width: '160px', margin: '24px auto'}} loop={false} animationData={deleteAnimation} />
          <Typography textAlign="center">Votre compte a bien été supprimé !</Typography>
          <Grid item mt={2}>
            <Button variant="contained" size="large" color="secondary" onClick={auth.logout}>
              Retour à l'accueil
            </Button>
          </Grid>
        </Grid>
      ) : (
        <>
          <Box p={3}>
            {userCanDeleteAccount ? (
              <Typography color="text.primary">Voulez-vous vraiment supprimer définitivement votre compte ?</Typography>
            ) : (
              <Typography>
                Vous devez supprimer toutes vos connexions depuis la page{' '}
                <Link style={{color: import.meta.env.VITE_SECONDARY_COLOR}} to={APP_ROUTES.connections}>
                  Gérer mes connexions
                </Link>{' '}
                avant de supprimer votre compte.
              </Typography>
            )}
          </Box>
          <DialogActions>
            <Button variant="outlined" size="large" color="primary" autoFocus onClick={resetState}>
              Annuler
            </Button>
            <Button disabled={!userCanDeleteAccount || isLoading} variant="contained" size="large" color="error" onClick={onConfirm}>
              Supprimer
            </Button>
          </DialogActions>
          <Box px={2} pb={2}>
            {deleteError && (
              <Typography
                sx={{
                  textAlign: 'center',
                  color: parentTheme.palette.error.main
                }}
              >
                {deleteError}
              </Typography>
            )}
          </Box>
        </>
      )}
    </BaseModal>
  );
};

DeleteProfileModal.propTypes = {
  onClose: func.isRequired,
  deleteProfile: func.isRequired,
  isOpen: bool
};

export default DeleteProfileModal;
